import { useContext, useState } from 'react';
import { Modal, Row, Col, Button, Alert } from 'react-bootstrap';

import AddCustomFields from './AddCustomFields';
import RetainerAgreement from './RetainerUploadComponent';
import { CustomField, DocumentRequestsModel, RetainerState } from '../../../types';
import StateContext from '../../../context/State';
import { DefaultDocumentRequests, defaultRetainerState } from '../../../data';
import PortalService from '../../../services/portal.service';
import getArrayBufferFromFile from "../../../../../util/getArrayBufferFromFile"
import DocumentRequestComponent from '../../../components/DocumentRequestComponent';
import { runInnerFunction } from '../../../../../compatManager';

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

export type NewClientPortalProps = {
    showModal: boolean;
    onHideModal: () => void;
}

const NewClientPortal = (props: NewClientPortalProps) => {
    const { state, setState } = useContext(StateContext);
    const portalService = new PortalService(state);
    const [uploadedFile, setUploadedFile] = useState<File>(null);
    const [clientPortalLoading, setClientPortalLoading] = useState<boolean>(false);
    const [customFields, setCustomFields] = useState<CustomField[]>([]);
    const [customFieldSetNames, setCustomFieldSetNames] = useState<string[]>([])
    const [retainerState, setRetainerState] = useState<RetainerState>(defaultRetainerState);
    const [documentRequests, setDocumentRequests] = useState<DocumentRequestsModel>(DefaultDocumentRequests);
    const [stepper, setStepper] = useState(0);

    const switchPage = (n: number) => {
        if (n === 1 && !isRetainerValid()) {
            return;
        }

        setStepper(n);
    }

    const isRetainerValid = (): boolean => {
        //if retainer is required, make sure other values are set
        if (retainerState.retainerRequired &&
            !retainerState.uploadRetainer &&
            !retainerState.textBoxRetainerRequired &&
            !retainerState.defaultRetainerRequired) {
            // MODAL GOES HERE
            alert('retainer is required but nothing is set ')
            setClientPortalLoading(false);
            return false;
        }

        // if a text based retainer is required, make sure the value is set
        if (retainerState.textBoxRetainerRequired) {
            if (retainerState.textBasedRetainer === '' || retainerState.textBasedRetainer === null) {
                // MODAL GOES HERE
                alert('Textbox cannot be empty! ');
                setClientPortalLoading(false);
                return false;
            }
        }

        // if a file was uploaded, make sure its pdf, then upload the file
        if (uploadedFile && retainerState.uploadRetainer) {
            if (uploadedFile.type !== 'application/pdf') {
                // MODAL GOES HERE
                alert('You can only upload Retainers in .pdf format');
                setClientPortalLoading(false)
                return false;
            }
        }

        return true;
    }

    const createPortal = async (): Promise<void> => {
        setClientPortalLoading(true);

        if (!state.appId) {
            alert('No file!');
            return;
        }

        try {
            if (!isRetainerValid()) {
                return;
            }

            if (uploadedFile && retainerState.uploadRetainer) {
                if (uploadedFile.type === 'application/pdf') {
                    await sendPDF(uploadedFile);
                }
            }

            const portalId = await portalService.createPortal();

            try {
                runInnerFunction('ClientPortalUpdateAndSaveFile', {
                    'initClientConnect': true,
                    'documentRequest': documentRequests.filter(d => d.documentName),
                    'clientConnectId': portalId,
                    'customFieldSets': customFieldSetNames
                });
                await setRetainerData(portalId);
                await setCustomFieldsData(portalId);
            } catch (e) {
                alert(e);
            }
        } catch (e) {
            console.log(e);
            // MODAL GOES HERE
            setClientPortalLoading(false)
            alert(`Unable to create Client Connect portal.`);
        }
    }

    const setRetainerData = async (portalId: number) => {
        await portalService.setRetainerType(retainerState, state.appId, portalId); // API CALL
    }

    const setCustomFieldsData = async (portalId) => {
        await portalService.setClientPortalCustomFields(portalId, customFields, 'beforeInit');
    }

    const sendPDF = async (file: any): Promise<void> => {
        try {
            const arrayBuffer: any = await getArrayBufferFromFile(file);
            await portalService.uploadDocument(arrayBuffer, state.portalId, state.appId, 'retainer.pdf', 'Retainer');
        } catch (e) {
            // MODAL GOES HERE
            console.log('unable to upload PDF: ', e)
        }
    }

    const steps = [
        'Written Retainer',
        'Intake Templates',
        'Document Requests',
        'Summary',
    ];


    return (
        <Modal
            show={props.showModal}
            onHide={props.onHideModal}
            size="lg"
            className="initiate-cc"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">Initiate ClientConnect</Modal.Title>
            </Modal.Header>

            <Modal.Body>

                <Row>
                    <Col>
                        <Box sx={{ width: '100%' }}>
                            <Stepper activeStep={stepper} alternativeLabel>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    </Col>
                </Row>
                <br />
                <Row>
                    { !stepper && 
                        <Col>
                            <RetainerAgreement
                                setRetainerState={setRetainerState}
                                retainerState={retainerState}
                                setUploadedFile={setUploadedFile}
                            />
                        </Col>
                    }
                    {stepper === 1 &&
                        <Col>
                            <AddCustomFields
                                setCustomFields={setCustomFields}
                                setCustomFieldSetNames={setCustomFieldSetNames}
                                customFieldSetNames={customFieldSetNames}
                                afterInit={false}
                            />
                        </Col>
                    }

                    {stepper === 2 &&
                        <Col>
                            <h3>Request Documents from Clients</h3>
                            <Alert>Enter the names of the documents you would like your client(s) to submit.</Alert>
                            <DocumentRequestComponent
                                setDocumentRequests={setDocumentRequests}
                            />
                        </Col>
                    }

                    {stepper === 3 &&
                        <Col>
                            <Alert>Initiating ClientConnect will invite your clients to submit their information via their primary contact email or phone number.</Alert>
                        </Col>
                    }

                </Row>
            </Modal.Body>

            <Modal.Footer>
                {stepper > 0 &&
                    <Button
                        variant="outline-primary"
                        onClick={() => switchPage(0)}
                    >
                        Start Over
                    </Button>
                }

                {stepper !== 3 &&
                    <Button
                        variant="outline-primary"
                        onClick={() => switchPage(3)}
                    >
                        Skip
                    </Button>
                }

                {stepper < 3 &&
                    <Button
                        variant="primary"
                        onClick={() => switchPage(stepper + 1)}
                    >
                        Next
                    </Button>
                }

                {stepper === 3 &&
                    <Button
                        variant="primary"
                        onClick={() => createPortal()}
                        disabled={clientPortalLoading}
                    >
                        Initiate
                    </Button>
                }
            </Modal.Footer>
        </Modal>
    );
}

export default NewClientPortal;
